import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { approvalApi } from "@erp/shared/api/generated/wrappers/approval";
import { queries } from "@erp/shared/queries";
import { showAlert, showConfirm } from "@erp/shared/utils/dialog";
import { getErrorMessage } from "@erp/shared/utils/error";
export function useApprovalAction({ documentType, documentId, onStatusChange, skipConfirm = false, }) {
    const queryClient = useQueryClient();
    const invalidateApproval = () => {
        void queryClient.invalidateQueries({
            queryKey: queries.approval._def,
        });
    };
    const approveMutation = useMutation({
        mutationFn: async ({ stepId, comment, }) => {
            return approvalApi.approveStep(stepId, comment);
        },
        onSuccess: (result) => {
            invalidateApproval();
            onStatusChange?.(result.documentStatus);
            showAlert("승인되었습니다.");
        },
        onError: (err) => {
            showAlert(getErrorMessage(err));
        },
    });
    const rejectMutation = useMutation({
        mutationFn: async ({ stepId, comment, }) => {
            return approvalApi.rejectStep(stepId, comment);
        },
        onSuccess: (result) => {
            invalidateApproval();
            onStatusChange?.(result.documentStatus);
            showAlert("반려되었습니다.");
        },
        onError: (err) => {
            showAlert(getErrorMessage(err));
        },
    });
    const resubmitMutation = useMutation({
        mutationFn: async () => {
            return approvalApi.resubmit(documentType, documentId);
        },
        onSuccess: (result) => {
            invalidateApproval();
            onStatusChange?.(result.documentStatus);
            showAlert("재상신되었습니다.");
        },
        onError: (err) => {
            showAlert(getErrorMessage(err));
        },
    });
    async function approve(stepId, comment) {
        if (!skipConfirm) {
            const confirmed = await showConfirm("승인하시겠습니까?");
            if (!confirmed)
                return false;
        }
        try {
            await approveMutation.mutateAsync({ stepId, comment });
            return true;
        }
        catch {
            return false;
        }
    }
    async function reject(stepId, comment) {
        if (!comment.trim()) {
            showAlert("반려 사유를 입력해주세요.");
            return false;
        }
        if (!skipConfirm) {
            const confirmed = await showConfirm("반려하시겠습니까?");
            if (!confirmed)
                return false;
        }
        try {
            await rejectMutation.mutateAsync({ stepId, comment });
            return true;
        }
        catch {
            return false;
        }
    }
    async function resubmit() {
        const confirmed = await showConfirm("재상신하시겠습니까?");
        if (!confirmed)
            return;
        resubmitMutation.mutate();
    }
    return {
        approve,
        reject,
        resubmit,
        isApproving: approveMutation.isPending,
        isRejecting: rejectMutation.isPending,
        isResubmitting: resubmitMutation.isPending,
    };
}
